/**
 * Flow Builder UI - Zapier/Make-style visual workflow editor.
 * Babel-safe: no nullish coalescing, optional chaining, optional catch, fragment shorthand.
 */
import { formatDateTime, getWorkspaceTimeZone, setWorkspaceTimeZone, zoneLabel } from './datetime.js';

(function () {
  'use strict';
  var React = window.React;
  var useState = React.useState;
  var useEffect = React.useEffect;
  var useCallback = React.useCallback;
  var useRef = React.useRef;
  var API_BASE = window.API_BASE || 'https://api.nightcoders.com';

  function API(method, url, body) {
    var token = window.localStorage.getItem('authToken');
    var headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = 'Bearer ' + token;
    return fetch(API_BASE + url, {
      method: method,
      headers: headers,
      body: body ? JSON.stringify(body) : undefined
    }).then(function (r) {
      if (!r.ok) return r.text().then(function (t) {
        var msg = '';
        try {
          var data = JSON.parse(t);
          msg = (data && (data.message || data.detail)) || '';
          if (!msg && data && typeof data.error === 'string') msg = data.error;
          if (msg && /^[a-z0-9_]+$/.test(msg)) msg = msg.replace(/_/g, ' ');
        } catch (e) {
          msg = t && t.length < 180 && t.indexOf('<') < 0 ? t : '';
        }
        throw new Error(msg || 'Something went wrong. Try again.');
      });
      if (r.status === 204) return {};
      return r.json().catch(function () { return {}; });
    });
  }

  function formatSafeDate(value) {
    return formatDateTime(value, { timeZone: getWorkspaceTimeZone(), style: 'smart' });
  }

  var NODE_TYPES = {
    trigger: { label: 'Trigger', category: 'triggers', icon: '▶', help: 'Defines when this flow starts (inbound/outbound call events).' },
    http_request: { label: 'HTTP Request', category: 'actions', icon: '🌐', help: 'Call an external API using an integration (auth + limits applied).' },
    set_variable: { label: 'Set Variable', category: 'actions', icon: '📝', help: 'Store values from previous steps to use later.' },
    condition: { label: 'Condition', category: 'logic', icon: '◇', help: 'Branch logic with if / else rules.' },
    knowledge_pack: { label: 'Knowledge Pack', category: 'actions', icon: '📦', help: 'Expose structured data to the AI during the call.' },
    record_outcome: { label: 'Record Outcome', category: 'metrics', icon: '✓', help: 'Write a call outcome (e.g. billing_inquiry, appointment_booked) for dashboard metrics.' },
    add_tag: { label: 'Add Tag', category: 'metrics', icon: '🏷', help: 'Add a tag to the call (e.g. authenticated, escalated).' },
    set_intent: { label: 'Set Intent', category: 'metrics', icon: '🎯', help: 'Record caller intent (e.g. pay_bill, schedule_service) for analytics.' },
    hubspot_upsert_contact: { label: 'HubSpot: Upsert Contact', category: 'integrations', icon: '🟠', help: 'Create or update a contact in HubSpot. Requires HubSpot connected in Integrations.' },
    hubspot_log_call: { label: 'HubSpot: Log Call', category: 'integrations', icon: '🟠', help: 'Log a call engagement to HubSpot. Requires HubSpot connected in Integrations.' },
    outlook_create_event: { label: 'Outlook: Create Calendar Event', category: 'integrations', icon: '📅', help: 'Create a calendar event in Outlook. Requires Outlook connected in Integrations.' },
    webhook_post: { label: 'Webhook: POST JSON', category: 'integrations', icon: '🔗', help: 'Send a JSON payload to a configured webhook URL.' }
  };

  var nodeHelp = {
    trigger: { title: 'Trigger', desc: 'Defines when this flow starts (inbound/outbound call events).' },
    http_request: { title: 'HTTP Request', desc: 'Call an external API using an integration (auth + limits applied).' },
    set_variable: { title: 'Set Variable', desc: 'Store values from previous steps to use later.' },
    condition: { title: 'Condition', desc: 'Branch logic with if / else rules.' },
    knowledge_pack: { title: 'Knowledge Pack', desc: 'Expose structured data to the AI during the call.' },
    record_outcome: { title: 'Record Outcome', desc: 'Write a call outcome for dashboard metrics.' },
    add_tag: { title: 'Add Tag', desc: 'Add a tag to the call.' },
    set_intent: { title: 'Set Intent', desc: 'Record caller intent for analytics.' },
    hubspot_upsert_contact: { title: 'HubSpot: Upsert Contact', desc: 'Uses your connected HubSpot account. Connect in Settings → Integrations if needed.' },
    hubspot_log_call: { title: 'HubSpot: Log Call', desc: 'Uses your connected HubSpot account. Connect in Settings → Integrations if needed.' },
    outlook_create_event: { title: 'Outlook: Create Calendar Event', desc: 'Uses your connected Outlook calendar. Connect in Settings → Integrations if needed.' },
    webhook_post: { title: 'Webhook: POST JSON', desc: 'Sends JSON to a webhook URL configured in Integrations.' }
  };

  var TRIGGER_OPTIONS = [
    { value: 'inbound_call_started', label: 'Inbound call started' },
    { value: 'inbound_call_ended', label: 'Inbound call ended' },
    { value: 'outbound_call_completed', label: 'Outbound call completed' },
    { value: 'lead_status_changed', label: 'Lead status changed' },
    { value: 'manual_test', label: 'Manual test' }
  ];

  function getTriggerLabel(value) {
    var o = TRIGGER_OPTIONS.find(function (opt) { return opt.value === value; });
    return (o && o.label) ? o.label : (value || 'Not set');
  }

  function generateId() {
    return 'n_' + Date.now() + '_' + Math.random().toString(36).slice(2, 9);
  }

  var GRID_SNAP = 16;
  var CANVAS_PADDING = 24;

  function snapToGrid(val) {
    return Math.round(val / GRID_SNAP) * GRID_SNAP;
  }

  var NODE_WIDTH = 180;
  var NODE_PORT_Y = 40;

  function getPortPosition(node, port) {
    if (port === 'in') {
      return { x: node.x, y: node.y + NODE_PORT_Y };
    }
    return { x: node.x + NODE_WIDTH, y: node.y + NODE_PORT_Y };
  }

  function FlowNode(props) {
    var node = props.node;
    var selected = props.selected;
    var onSelect = props.onSelect;
    var onDragStart = props.onDragStart;
    var onPortMouseDown = props.onPortMouseDown;
    var hasError = props.hasError;
    var connectingFrom = props.connectingFrom;
    var meta = NODE_TYPES[node.type] || { label: node.type, icon: '?' };
    var hasInput = node.type !== 'trigger';
    var canAcceptInput = hasInput && connectingFrom && connectingFrom.id !== node.id;
    return React.createElement('div', {
      className: 'flow-node' + (selected ? ' flow-node--selected' : '') + (hasError ? ' flow-node--error' : ''),
      style: { left: node.x + 'px', top: node.y + 'px', width: NODE_WIDTH + 'px' },
      onClick: function (e) { e.stopPropagation(); onSelect(node); },
      onMouseDown: function (e) { if (e.button === 0) onDragStart(e, node); }
    },
      React.createElement('div', { className: 'flow-node-header' },
        React.createElement('span', { className: 'flow-node-icon' }, meta.icon),
        React.createElement('span', { className: 'flow-node-label' }, meta.label)
      ),
      React.createElement('div', { className: 'flow-node-ports' },
        hasInput ? React.createElement('div', {
          className: 'flow-port flow-port-in' + (canAcceptInput ? ' flow-port--valid-target' : ''),
          'data-port': 'in',
          'data-node-id': node.id,
          title: canAcceptInput ? 'Connect to…' : ''
        }) : null,
        React.createElement('div', {
          className: 'flow-port flow-port-out',
          'data-port': 'out',
          'data-node-id': node.id,
          onMouseDown: function (e) {
            e.stopPropagation();
            if (e.button === 0 && onPortMouseDown) onPortMouseDown(e, node);
          },
          title: 'Drag to connect'
        })
      )
    );
  }

  function FlowCanvas(props) {
    var nodes = props.nodes;
    var edges = props.edges;
    var selectedId = props.selectedId;
    var selectedEdgeId = props.selectedEdgeId;
    var onSelect = props.onSelect;
    var onSelectEdge = props.onSelectEdge;
    var onUpdateNode = props.onUpdateNode;
    var onDrop = props.onDrop;
    var onConnect = props.onConnect;
    var onDeleteEdge = props.onDeleteEdge;
    var validateNode = props.validateNode || function () { return false; };

    var containerRef = useRef(null);
    var dragRef = useRef(null);
    var panRef = useRef(null);

    var _pan = useState({ x: 0, y: 0 });
    var pan = _pan[0];
    var setPan = _pan[1];
    var _scale = useState(1);
    var scale = _scale[0];
    var setScale = _scale[1];
    var _isPanning = useState(false);
    var isPanning = _isPanning[0];
    var setIsPanning = _isPanning[1];
    var _connectingFrom = useState(null);
    var connectingFrom = _connectingFrom[0];
    var setConnectingFrom = _connectingFrom[1];
    var _connectPreviewPos = useState(null);
    var connectPreviewPos = _connectPreviewPos[0];
    var setConnectPreviewPos = _connectPreviewPos[1];

    function clientToCanvas(clientX, clientY) {
      var rect = containerRef.current ? containerRef.current.getBoundingClientRect() : { left: 0, top: 0 };
      return {
        x: (clientX - rect.left - CANVAS_PADDING - pan.x) / scale,
        y: (clientY - rect.top - CANVAS_PADDING - pan.y) / scale
      };
    }

    function handlePortMouseDown(e, node) {
      e.preventDefault();
      setConnectingFrom(node);
    }

    function handleCanvasClick(e) {
      var t = e.target;
      var isCanvas = t === e.currentTarget || (t.classList && t.classList.contains('flow-canvas-pannable')) || (t.classList && t.classList.contains('flow-canvas-inner'));
      var isEdgesLayer = t.closest && t.closest('.flow-edges-layer') && !(t.closest && t.closest('.flow-edge-hit'));
      if (isCanvas || isEdgesLayer) {
        onSelect(null);
        if (onSelectEdge) onSelectEdge(null);
      }
    }

    function handleCanvasMouseDown(e) {
      if (e.button !== 0) return;
      if (e.target.closest && e.target.closest('.flow-node')) return;
      if (e.target.closest && e.target.closest('.flow-port')) return;
      panRef.current = {
        startX: e.clientX - pan.x,
        startY: e.clientY - pan.y
      };
      setIsPanning(true);
    }

    function handleWheel(e) {
      // Plain wheel scrolls the page; Ctrl/Cmd+wheel and trackpad pinch (reported as ctrlKey) zoom.
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      var delta = e.deltaY > 0 ? -0.1 : 0.1;
      setScale(function (s) {
        var next = s + delta;
        if (next < 0.5) return 0.5;
        if (next > 2) return 2;
        return next;
      });
    }

    useEffect(function () {
      var el = containerRef.current;
      if (!el) return;
      el.addEventListener('wheel', handleWheel, { passive: false });
      return function () { el.removeEventListener('wheel', handleWheel); };
    }, []);

    useEffect(function () {
      if (!isPanning || !panRef.current) return;
      function onMove(e) {
        if (!panRef.current) return;
        var newPanX = e.clientX - panRef.current.startX;
        var newPanY = e.clientY - panRef.current.startY;
        setPan({ x: newPanX, y: newPanY });
      }
      function onUp() {
        panRef.current = null;
        setIsPanning(false);
      }
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      return function () {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
    }, [isPanning]);

    useEffect(function () {
      if (!connectingFrom) return;
      function onMove(e) {
        setConnectPreviewPos(clientToCanvas(e.clientX, e.clientY));
      }
      function onUp(e) {
        var el = document.elementFromPoint(e.clientX, e.clientY);
        var portEl = el && el.closest ? el.closest('[data-port="in"][data-node-id]') : null;
        var targetNodeId = portEl ? portEl.getAttribute('data-node-id') : null;
        if (targetNodeId && onConnect && connectingFrom.id !== targetNodeId) {
          onConnect(connectingFrom.id, targetNodeId);
        }
        setConnectingFrom(null);
        setConnectPreviewPos(null);
      }
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      return function () {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
    }, [connectingFrom, onConnect, pan.x, pan.y, scale]);

    useEffect(function () {
      function onKeyDown(e) {
        if (e.key === 'Delete' || e.key === 'Backspace') {
          if (selectedEdgeId && onDeleteEdge) {
            e.preventDefault();
            onDeleteEdge(selectedEdgeId);
            if (onSelectEdge) onSelectEdge(null);
          }
        }
      }
      document.addEventListener('keydown', onKeyDown);
      return function () { document.removeEventListener('keydown', onKeyDown); };
    }, [selectedEdgeId, onDeleteEdge, onSelectEdge]);

    function handleDrop(e) {
      e.preventDefault();
      var data;
      try {
        data = JSON.parse(e.dataTransfer.getData('application/json') || '{}');
      } catch (err) { return; }
      if (!data.type) return;
      var rect = containerRef.current ? containerRef.current.getBoundingClientRect() : { left: 0, top: 0 };
      var canvasX = (e.clientX - rect.left - CANVAS_PADDING - pan.x) / scale - 100;
      var canvasY = (e.clientY - rect.top - CANVAS_PADDING - pan.y) / scale - 30;
      var x = snapToGrid(canvasX);
      var y = snapToGrid(canvasY);
      onDrop(data.type, Math.max(0, x), Math.max(0, y));
    }

    function handleDragOver(e) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }

    function handleNodeDragStart(e, node) {
      if (e.target.closest && e.target.closest('.flow-port')) return;
      e.preventDefault();
      var rect = containerRef.current ? containerRef.current.getBoundingClientRect() : { left: 0, top: 0 };
      var canvasX = (e.clientX - rect.left - CANVAS_PADDING - pan.x) / scale;
      var canvasY = (e.clientY - rect.top - CANVAS_PADDING - pan.y) / scale;
      dragRef.current = {
        node: node,
        offsetX: canvasX - node.x,
        offsetY: canvasY - node.y
      };
    }

    useEffect(function () {
      function onMove(e) {
        if (!dragRef.current) return;
        var rect = containerRef.current ? containerRef.current.getBoundingClientRect() : { left: 0, top: 0 };
        var canvasX = (e.clientX - rect.left - CANVAS_PADDING - pan.x) / scale;
        var canvasY = (e.clientY - rect.top - CANVAS_PADDING - pan.y) / scale;
        var newX = snapToGrid(canvasX - dragRef.current.offsetX);
        var newY = snapToGrid(canvasY - dragRef.current.offsetY);
        onUpdateNode(dragRef.current.node.id, { x: newX, y: newY });
      }
      function onUp() {
        dragRef.current = null;
      }
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      return function () {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
    }, [onUpdateNode, pan.x, pan.y, scale]);

    function bezierPath(from, to) {
      var dx = Math.abs(to.x - from.x) * 0.5;
      if (dx < 40) dx = 40;
      return 'M ' + from.x + ' ' + from.y + ' C ' + (from.x + dx) + ' ' + from.y + ' ' + (to.x - dx) + ' ' + to.y + ' ' + to.x + ' ' + to.y;
    }

    var edgeLines = edges.map(function (e) {
      var src = nodes.find(function (n) { return n.id === e.source; });
      var tgt = nodes.find(function (n) { return n.id === e.target; });
      if (!src || !tgt) return null;
      var from = getPortPosition(src, 'out');
      var to = getPortPosition(tgt, 'in');
      var pathD = bezierPath(from, to);
      var isSelected = selectedEdgeId === e.id;
      return React.createElement('g', {
        key: e.id,
        className: 'flow-edge-group' + (isSelected ? ' flow-edge--selected' : '')
      },
        React.createElement('path', {
          d: pathD,
          className: 'flow-edge flow-edge-hit',
          'data-edge-id': e.id,
          onClick: function (ev) {
            ev.stopPropagation();
            if (onSelectEdge) onSelectEdge(e.id);
          }
        }),
        React.createElement('path', {
          d: pathD,
          className: 'flow-edge flow-edge-visual',
          markerEnd: 'url(#flow-arrow)'
        })
      );
    });

    var previewPath = connectingFrom && connectPreviewPos
      ? bezierPath(getPortPosition(connectingFrom, 'out'), connectPreviewPos)
      : null;

    var showEmptyHelper = nodes.length === 0 || (nodes.length === 1 && edges.length === 0);
    var emptyCanvasOverlay = showEmptyHelper ? React.createElement('div', {
      className: 'flow-empty-helper',
      style: { pointerEvents: 'none' }
    },
      React.createElement('h3', { className: 'flow-empty-helper-title' }, 'Start by adding a Trigger node'),
      React.createElement('ul', { className: 'flow-empty-helper-list' },
        React.createElement('li', null, 'Drag Trigger from the left panel'),
        React.createElement('li', null, 'Add HTTP Request, Set Variable, or Condition'),
        React.createElement('li', null, 'Connect nodes by dragging from output to input ports')
      ),
      React.createElement('p', { className: 'flow-empty-helper-tip' }, 'Or choose a template from the dropdown above.')
    ) : null;

    var canvasCursor = isPanning ? 'grabbing' : 'grab';

    return React.createElement('div', {
      className: 'flow-canvas',
      ref: containerRef,
      onDrop: handleDrop,
      onDragOver: handleDragOver,
      onClick: handleCanvasClick,
      onMouseDown: handleCanvasMouseDown,
      style: { cursor: canvasCursor }
    },
      React.createElement('div', {
        className: 'flow-canvas-pannable',
        style: {
          transform: 'translate(' + pan.x + 'px, ' + pan.y + 'px) scale(' + scale + ')',
          transformOrigin: '0 0'
        }
      },
        React.createElement('div', { className: 'flow-canvas-inner' },
          React.createElement('svg', { className: 'flow-edges-layer' },
            React.createElement('defs', null,
              React.createElement('marker', {
                id: 'flow-arrow',
                markerWidth: 8,
                markerHeight: 6,
                refX: 7,
                refY: 3,
                orient: 'auto'
              },
                React.createElement('path', { d: 'M0,0 L8,3 L0,6 Z', fill: 'var(--accent1)' })
              )
            ),
            edgeLines,
            previewPath ? React.createElement('path', {
              d: previewPath,
              className: 'flow-edge flow-edge-preview'
            }) : null
          ),
          emptyCanvasOverlay,
          nodes.map(function (n) {
            return React.createElement(FlowNode, {
              key: n.id,
              node: n,
              selected: selectedId === n.id,
              onSelect: onSelect,
              onDragStart: handleNodeDragStart,
              onPortMouseDown: handlePortMouseDown,
              connectingFrom: connectingFrom,
              hasError: validateNode(n)
            });
          })
        )
      )
    );
  }

  function NodeInspector(props) {
    var node = props.node;
    var integrations = props.integrations || [];
    var onChange = props.onChange;
    var onNavigate = props.onNavigate;
    if (!node) {
      return React.createElement('div', { className: 'flow-inspector flow-inspector--empty' },
        React.createElement('p', { className: 'muted' }, 'Select a node to configure it.')
      );
    }
    var meta = NODE_TYPES[node.type] || { label: node.type };
    var help = nodeHelp[node.type] || { title: 'Node', desc: '' };
    var cfg = node.config || {};

    function update(key, value) {
      var next = Object.assign({}, cfg, { [key]: value });
      onChange(Object.assign({}, node, { config: next }));
    }

    if (node.type === 'trigger') {
      return React.createElement('div', { className: 'flow-inspector' },
        React.createElement('h4', null, meta.label),
        React.createElement('label', { className: 'field' },
          React.createElement('span', { className: 'field-label' }, 'Trigger type'),
          React.createElement('select', {
            value: cfg.trigger_type || 'inbound_call_started',
            onChange: function (e) { update('trigger_type', e.target.value); }
          },
            TRIGGER_OPTIONS.map(function (o) {
              return React.createElement('option', { key: o.value, value: o.value }, o.label);
            })
          )
        )
      );
    }

    if (node.type === 'http_request') {
      var integration = cfg.integration_id ? integrations.find(function (i) { return i.id === cfg.integration_id; }) : null;
      var baseUrl = integration && integration.base_url ? String(integration.base_url).replace(/\/+$/, '') : '';
      var pathVal = (cfg.path || '/').trim();
      var pathForUrl = pathVal && pathVal.indexOf('/') !== 0 ? '/' + pathVal : (pathVal || '/');
      var fullUrl = baseUrl ? (baseUrl + pathForUrl) : '';
      var hasNoIntegration = !cfg.integration_id;
      return React.createElement('div', { className: 'flow-inspector flow-inspector--http' },
        React.createElement('h4', null, meta.label),
        help.desc ? React.createElement('p', { className: 'flow-inspector-help muted' }, help.desc) : null,
        hasNoIntegration ? React.createElement('div', { className: 'flow-http-warning' },
          React.createElement('span', { className: 'flow-http-warning-icon' }, '⚠'),
          React.createElement('span', null, 'This node requires an integration to run.')
        ) : null,
        React.createElement('label', { className: 'field' },
          React.createElement('span', { className: 'field-label' }, 'Integration'),
          React.createElement('select', {
            value: cfg.integration_id || '',
            onChange: function (e) { update('integration_id', parseInt(e.target.value, 10) || null); }
          },
            React.createElement('option', { value: '' }, 'Select…'),
            (Array.isArray(integrations) ? integrations : []).map(function (i) {
              return React.createElement('option', { key: i.id, value: i.id }, i.name);
            })
          )
        ),
        hasNoIntegration ? React.createElement('p', { className: 'flow-inspector-hint muted' }, 'Select an integration to define the Base URL.') : null,
        cfg.integration_id ? React.createElement('div', { className: 'flow-baseurl-block' },
          React.createElement('label', { className: 'field' },
            React.createElement('span', { className: 'field-label' }, 'Base URL'),
            React.createElement('input', {
              type: 'text',
              readOnly: true,
              value: baseUrl,
              className: 'input-readonly flow-baseurl-readonly'
            })
          ),
          React.createElement('p', { className: 'flow-baseurl-helper muted' }, 'Base URL is configured in External Integrations.'),
          onNavigate ? React.createElement('button', {
            type: 'button',
            className: 'button outline small',
            style: { marginTop: 6 },
            onClick: function () { onNavigate('integrations'); }
          }, 'Manage integrations') : null
        ) : null,
        React.createElement('label', { className: 'field field-row-inline' },
            React.createElement('span', { className: 'field-label' }, 'Method'),
            React.createElement('select', {
              value: cfg.method || 'GET',
              onChange: function (e) { update('method', e.target.value); },
              style: { width: 80 }
            },
              React.createElement('option', { value: 'GET' }, 'GET'),
              React.createElement('option', { value: 'POST' }, 'POST'),
              React.createElement('option', { value: 'PUT' }, 'PUT'),
              React.createElement('option', { value: 'PATCH' }, 'PATCH')
            )
          ),
          React.createElement('label', { className: 'field' },
            React.createElement('span', { className: 'field-label' }, 'Request path'),
            React.createElement('input', {
              type: 'text',
              value: cfg.path || '/',
              onChange: function (e) { update('path', e.target.value); },
              onBlur: function (e) {
                var v = (e.target.value || '').trim();
                if (!v) {
                  update('path', '/');
                } else if (v.indexOf('/') !== 0) {
                  update('path', '/' + v);
                }
              },
              placeholder: '/v1/customers/lookup'
            })
          ),
        fullUrl ? React.createElement('div', { className: 'flow-inspector-fullurl' },
          React.createElement('span', { className: 'field-label' }, 'Full URL'),
          React.createElement('code', { className: 'flow-fullurl-preview' }, fullUrl)
        ) : null,
        React.createElement('label', { className: 'field' },
          React.createElement('span', { className: 'field-label' }, 'Response map (JSON)'),
          React.createElement('textarea', {
            rows: 3,
            value: typeof cfg.responseMap === 'object' ? JSON.stringify(cfg.responseMap, null, 2) : (cfg.responseMap || '{}'),
            onChange: function (e) {
              try {
                update('responseMap', JSON.parse(e.target.value || '{}'));
              } catch (err) {}
            },
            placeholder: '{"accountId": "data.id"}'
          })
        )
      );
    }

    if (node.type === 'set_variable') {
      return React.createElement('div', { className: 'flow-inspector' },
        React.createElement('h4', null, meta.label),
        help.desc ? React.createElement('p', { className: 'flow-inspector-help muted' }, help.desc) : null,
        React.createElement('label', { className: 'field' },
          React.createElement('span', { className: 'field-label' }, 'Variable key'),
          React.createElement('input', {
            type: 'text',
            value: cfg.key || '',
            onChange: function (e) { update('key', e.target.value); },
            placeholder: 'accountId'
          })
        ),
        React.createElement('label', { className: 'field' },
          React.createElement('span', { className: 'field-label' }, 'Value (use {{ctx.fromNumber}} for template)'),
          React.createElement('input', {
            type: 'text',
            value: cfg.value || '',
            onChange: function (e) { update('value', e.target.value); },
            placeholder: '{{vars.someKey}}'
          })
        )
      );
    }

    if (node.type === 'condition') {
      return React.createElement('div', { className: 'flow-inspector' },
        React.createElement('h4', null, meta.label),
        React.createElement('label', { className: 'field' },
          React.createElement('span', { className: 'field-label' }, 'Variable path'),
          React.createElement('input', {
            type: 'text',
            value: cfg.variable || '',
            onChange: function (e) { update('variable', e.target.value); },
            placeholder: 'vars.accountId'
          })
        ),
        React.createElement('label', { className: 'field' },
          React.createElement('span', { className: 'field-label' }, 'Operator'),
          React.createElement('select', {
            value: cfg.operator || 'equals',
            onChange: function (e) { update('operator', e.target.value); }
          },
            ['equals', 'not_equals', 'contains', 'exists', 'not_exists', 'gt', 'gte', 'lt', 'lte'].map(function (op) {
              return React.createElement('option', { key: op, value: op }, op);
            })
          )
        ),
        React.createElement('label', { className: 'field' },
          React.createElement('span', { className: 'field-label' }, 'Value'),
          React.createElement('input', {
            type: 'text',
            value: cfg.value != null ? String(cfg.value) : '',
            onChange: function (e) { update('value', e.target.value); }
          })
        )
      );
    }

    if (node.type === 'knowledge_pack') {
      return React.createElement('div', { className: 'flow-inspector' },
        React.createElement('h4', null, meta.label),
        help.desc ? React.createElement('p', { className: 'flow-inspector-help muted' }, help.desc) : null,
        React.createElement('label', { className: 'field' },
          React.createElement('span', { className: 'field-label' }, 'Payload (JSON, or use {{vars.x}})'),
          React.createElement('textarea', {
            rows: 4,
            value: typeof cfg.payload === 'object' ? JSON.stringify(cfg.payload, null, 2) : (cfg.payload || '{}'),
            onChange: function (e) {
              try {
                var v = e.target.value.trim();
                update('payload', v ? JSON.parse(v) : {});
              } catch (err) {}
            },
            placeholder: '{"account": "{{vars.accountId}}"}'
          })
        )
      );
    }

    if (node.type === 'record_outcome') {
      return React.createElement('div', { className: 'flow-inspector' },
        React.createElement('h4', null, meta.label),
        help.desc ? React.createElement('p', { className: 'flow-inspector-help muted' }, help.desc) : null,
        React.createElement('label', { className: 'field' },
          React.createElement('span', { className: 'field-label' }, 'Outcome code'),
          React.createElement('input', {
            type: 'text',
            value: cfg.outcome_code || '',
            onChange: function (e) { update('outcome_code', e.target.value); },
            placeholder: 'billing_inquiry'
          })
        ),
        React.createElement('label', { className: 'field' },
          React.createElement('span', { className: 'field-label' }, 'Source'),
          React.createElement('select', {
            value: cfg.source || 'flow',
            onChange: function (e) { update('source', e.target.value); }
          },
            React.createElement('option', { value: 'flow' }, 'flow'),
            React.createElement('option', { value: 'ai' }, 'ai'),
            React.createElement('option', { value: 'agent' }, 'agent')
          )
        )
      );
    }

    if (node.type === 'add_tag') {
      return React.createElement('div', { className: 'flow-inspector' },
        React.createElement('h4', null, meta.label),
        help.desc ? React.createElement('p', { className: 'flow-inspector-help muted' }, help.desc) : null,
        React.createElement('label', { className: 'field' },
          React.createElement('span', { className: 'field-label' }, 'Tag'),
          React.createElement('input', {
            type: 'text',
            value: cfg.tag || '',
            onChange: function (e) { update('tag', e.target.value); },
            placeholder: 'authenticated'
          })
        )
      );
    }

    if (node.type === 'set_intent') {
      return React.createElement('div', { className: 'flow-inspector' },
        React.createElement('h4', null, meta.label),
        help.desc ? React.createElement('p', { className: 'flow-inspector-help muted' }, help.desc) : null,
        React.createElement('label', { className: 'field' },
          React.createElement('span', { className: 'field-label' }, 'Intent'),
          React.createElement('input', {
            type: 'text',
            value: cfg.intent || '',
            onChange: function (e) { update('intent', e.target.value); },
            placeholder: 'pay_bill'
          })
        )
      );
    }

    if (node.type === 'hubspot_upsert_contact' || node.type === 'hubspot_log_call' || node.type === 'outlook_create_event' || node.type === 'webhook_post') {
      return React.createElement('div', { className: 'flow-inspector' },
        React.createElement('h4', null, meta.label),
        help.desc ? React.createElement('p', { className: 'flow-inspector-help muted' }, help.desc) : null,
        React.createElement('p', { className: 'muted', style: { fontSize: '0.8rem', marginTop: 8 } },
          'Configure and connect in Settings → Integrations. Event Settings control which events are sent; this node runs when the flow executes.'
        ),
        React.createElement('a', { href: '#integrations', style: { marginTop: 8, display: 'inline-block', fontSize: '0.875rem' } }, 'Open Integrations →')
      );
    }

    return React.createElement('div', { className: 'flow-inspector' }, React.createElement('p', null, 'No config for ' + node.type));
  }

  function FlowBuilderView(props) {
    var flow = props.flow;
    var onBack = props.onBack;
    var onSaved = props.onSaved;

    var integrations = props.integrations || [];
    var graph = flow && flow.graph_json ? (typeof flow.graph_json === 'string' ? JSON.parse(flow.graph_json || '{}') : flow.graph_json) : { nodes: [], edges: [] };
    var nodes = graph.nodes || [];
    var edges = graph.edges || [];

    var _useState = useState(flow ? flow.name : 'New Flow');
    var name = _useState[0];
    var setName = _useState[1];
    var _useState3 = useState(flow ? flow.direction : 'both');
    var direction = _useState3[0];
    var setDirection = _useState3[1];
    var _useState4 = useState(flow ? !!flow.is_enabled : true);
    var isEnabled = _useState4[0];
    var setIsEnabled = _useState4[1];
    var _useState5 = useState(nodes);
    var localNodes = _useState5[0];
    var setLocalNodes = _useState5[1];
    var _useState6 = useState(edges);
    var localEdges = _useState6[0];
    var setLocalEdges = _useState6[1];
    var _useState7 = useState(null);
    var selectedId = _useState7[0];
    var setSelectedId = _useState7[1];
    var _useState7b = useState(null);
    var selectedEdgeId = _useState7b[0];
    var setSelectedEdgeId = _useState7b[1];
    var _useState8 = useState(false);
    var dirty = _useState8[0];
    var setDirty = _useState8[1];
    var _useState9 = useState(null);
    var saveError = _useState9[0];
    var setSaveError = _useState9[1];
    var _useState10 = useState(null);
    var testResult = _useState10[0];
    var setTestResult = _useState10[1];
    var _useState11 = useState('list');
    var tab = _useState11[0];
    var setTab = _useState11[1];

    var historyRef = useRef([]);
    var historyIdxRef = useRef(-1);

    function pushHistory() {
      var snap = { nodes: JSON.parse(JSON.stringify(localNodes)), edges: JSON.parse(JSON.stringify(localEdges)) };
      var h = historyRef.current;
      var idx = historyIdxRef.current;
      h = h.slice(0, idx + 1);
      h.push(snap);
      if (h.length > 50) h.shift();
      historyRef.current = h;
      historyIdxRef.current = h.length - 1;
    }

    function undo() {
      var h = historyRef.current;
      var idx = historyIdxRef.current;
      if (idx <= 0) return;
      idx--;
      var snap = h[idx];
      setLocalNodes(snap.nodes);
      setLocalEdges(snap.edges);
      historyIdxRef.current = idx;
    }

    function redo() {
      var h = historyRef.current;
      var idx = historyIdxRef.current;
      if (idx >= h.length - 1) return;
      idx++;
      var snap = h[idx];
      setLocalNodes(snap.nodes);
      setLocalEdges(snap.edges);
      historyIdxRef.current = idx;
    }

    function validateNode(node) {
      if (node.type === 'http_request') {
        var c = node.config || {};
        return !c.integration_id || !(c.path && c.path.trim());
      }
      if (node.type === 'set_variable') {
        return !(node.config && node.config.key && String(node.config.key).trim());
      }
      return false;
    }

    function addNode(type, x, y) {
      pushHistory();
      var existingTrigger = localNodes.find(function (x) { return x.type === 'trigger'; });
      var defaultTrigger = existingTrigger && existingTrigger.config && existingTrigger.config.trigger_type
        ? existingTrigger.config.trigger_type
        : 'inbound_call_started';
      var n = {
        id: generateId(),
        type: type,
        x: x,
        y: y,
        config: type === 'trigger' ? { trigger_type: defaultTrigger } : {}
      };
      if (type === 'trigger' && existingTrigger) {
        return;
      }
      setLocalNodes(localNodes.concat([n]));
      setSelectedId(n.id);
      setDirty(true);
    }

    function updateNode(id, patch) {
      setLocalNodes(localNodes.map(function (n) {
        if (n.id !== id) return n;
        return Object.assign({}, n, patch);
      }));
      setDirty(true);
    }

    function deleteNode(id) {
      pushHistory();
      setLocalNodes(localNodes.filter(function (n) { return n.id !== id; }));
      setLocalEdges(localEdges.filter(function (e) { return e.source !== id && e.target !== id; }));
      if (selectedId === id) setSelectedId(null);
      setDirty(true);
    }

    function addEdge(source, target) {
      if (source === target) return;
      if (localEdges.some(function (e) { return e.source === source && e.target === target; })) return;
      pushHistory();
      var next = localEdges.filter(function (e) { return e.target !== target; });
      next = next.concat([{ id: 'e_' + generateId(), source: source, target: target }]);
      setLocalEdges(next);
      setDirty(true);
    }

    function deleteEdge(edgeId) {
      pushHistory();
      setLocalEdges(localEdges.filter(function (e) { return e.id !== edgeId; }));
      if (selectedEdgeId === edgeId) setSelectedEdgeId(null);
      setDirty(true);
    }

    function save() {
      setSaveError(null);
      var graphData = { nodes: localNodes, edges: localEdges };
      var triggerTypeToSave = derivedTriggerType || 'inbound_call_started';
      var payload = {
        name: name.trim(),
        is_enabled: isEnabled ? 1 : 0,
        direction: direction,
        trigger_type: triggerTypeToSave,
        graph_json: graphData
      };
      var url = (flow && flow.id) ? '/api/flows/' + flow.id : '/api/flows';
      var method = (flow && flow.id) ? 'PATCH' : 'POST';
      API(method, url, payload).then(function (saved) {
        setDirty(false);
        onSaved(saved);
      }).catch(function (e) {
        setSaveError((e && e.message) || 'Save failed');
      });
    }

    function runTest() {
      setTestResult(null);
      var graphData = { nodes: localNodes, edges: localEdges };
      var flowId = (flow && flow.id) ? flow.id : null;
      if (!flowId) {
        setTestResult({ error: 'Save the flow first before testing' });
        return;
      }
      API('POST', '/api/flows/' + flowId + '/test-run', {
        ctx: {
          tenantId: 1,
          callSid: 'CA_test_' + Date.now(),
          fromNumber: '+15551234567',
          toNumber: '+15559876543',
          direction: 'inbound'
        }
      }).then(function (r) {
        setTestResult(r);
      }).catch(function (e) {
        setTestResult({ ok: false, error: (e && e.message) || 'Test failed' });
      });
    }

    function ensureDemoIntegration(cb) {
      if (integrations.length > 0) {
        cb(integrations[0].id);
        return;
      }
      API('POST', '/api/external-integrations', {
        name: 'Demo API',
        base_url: 'https://httpbin.org',
        allowed_hosts: ['httpbin.org'],
        allowed_path_prefixes: ['/'],
        auth_type: 'none',
        rpm_limit: 60,
        daily_quota: 1000
      }).then(function (res) {
        var created = (res && res.integration) ? res.integration : res;
        if (props.onIntegrationsRefresh) props.onIntegrationsRefresh();
        cb(created && created.id ? created.id : null);
      }).catch(function () {
        cb(null);
      });
    }

    function applyTemplate(tpl) {
      if (tpl === 'inbound_auth_fetch') {
        ensureDemoIntegration(function (integrationId) {
          pushHistory();
          var t = { id: generateId(), type: 'trigger', x: 50, y: 50, config: { trigger_type: 'inbound_call_started' } };
          var h1 = { id: generateId(), type: 'http_request', x: 50, y: 160, config: { integration_id: integrationId, method: 'POST', path: '/post', responseMap: { customerId: '$.json.customerId', status: '$.json.status' } } };
          var cond = { id: generateId(), type: 'condition', x: 50, y: 290, config: { variable: 'vars.status', operator: 'equals', value: 'authenticated' } };
          var h2 = { id: generateId(), type: 'http_request', x: 50, y: 420, config: { integration_id: integrationId, method: 'GET', path: '/get?balance=100&due_date=2025-01-15', responseMap: { balance: '$.args.balance', dueDate: '$.args.due_date' } } };
          var sv = { id: generateId(), type: 'set_variable', x: 50, y: 550, config: { key: 'greeting', value: 'Your current balance is {{vars.balance}} and is due on {{vars.dueDate}}' } };
          setLocalNodes([t, h1, cond, h2, sv]);
          setLocalEdges([
            { id: 'e1', source: t.id, target: h1.id },
            { id: 'e2', source: h1.id, target: cond.id },
            { id: 'e3', source: cond.id, target: h2.id, sourceHandle: 'true' },
            { id: 'e4', source: h2.id, target: sv.id }
          ]);
          setDirty(true);
        });
      } else if (tpl === 'outbound_notify') {
        ensureDemoIntegration(function (integrationId) {
          pushHistory();
          var t2 = { id: generateId(), type: 'trigger', x: 50, y: 50, config: { trigger_type: 'outbound_call_completed' } };
          var h2 = { id: generateId(), type: 'http_request', x: 50, y: 180, config: { integration_id: integrationId, method: 'POST', path: '/post', responseMap: { ok: '$.success' } } };
          setLocalNodes([t2, h2]);
          setLocalEdges([{ id: 'e1', source: t2.id, target: h2.id }]);
          setDirty(true);
        });
      } else if (tpl === 'inbound_auth') {
        pushHistory();
        var firstInt = integrations[0] ? integrations[0].id : null;
        var t = { id: generateId(), type: 'trigger', x: 50, y: 50, config: { trigger_type: 'inbound_call_started' } };
        var h = { id: generateId(), type: 'http_request', x: 50, y: 180, config: { integration_id: firstInt, method: 'GET', path: '/api/account', responseMap: {} } };
        var k = { id: generateId(), type: 'knowledge_pack', x: 50, y: 310, config: { payload: {} } };
        setLocalNodes([t, h, k]);
        setLocalEdges([
          { id: 'e1', source: t.id, target: h.id },
          { id: 'e2', source: h.id, target: k.id }
        ]);
        setDirty(true);
      } else if (tpl === 'outbound_webhook') {
        pushHistory();
        var t2 = { id: generateId(), type: 'trigger', x: 50, y: 50, config: { trigger_type: 'outbound_call_completed' } };
        var k2 = { id: generateId(), type: 'knowledge_pack', x: 50, y: 180, config: { payload: {} } };
        setLocalNodes([t2, k2]);
        setLocalEdges([{ id: 'e1', source: t2.id, target: k2.id }]);
        setDirty(true);
      } else if (tpl === 'utilities_auth_bill') {
        ensureDemoIntegration(function (integrationId) {
          pushHistory();
          var t = { id: generateId(), type: 'trigger', x: 50, y: 50, config: { trigger_type: 'inbound_call_started' } };
          var h = { id: generateId(), type: 'http_request', x: 50, y: 160, config: { integration_id: integrationId, method: 'GET', path: '/get?phone={{fromNumber}}', responseMap: { accountId: '$.args.phone', balance: '$.args.balance' } } };
          var tag = { id: generateId(), type: 'add_tag', x: 50, y: 280, config: { tag: 'authenticated' } };
          var out = { id: generateId(), type: 'record_outcome', x: 50, y: 400, config: { outcome_code: 'billing_inquiry', source: 'flow' } };
          setLocalNodes([t, h, tag, out]);
          setLocalEdges([
            { id: 'e1', source: t.id, target: h.id },
            { id: 'e2', source: h.id, target: tag.id },
            { id: 'e3', source: tag.id, target: out.id }
          ]);
          setDirty(true);
        });
      } else if (tpl === 'sales_qualify_callback') {
        pushHistory();
        var t = { id: generateId(), type: 'trigger', x: 50, y: 50, config: { trigger_type: 'outbound_call_completed' } };
        var intent = { id: generateId(), type: 'set_intent', x: 50, y: 160, config: { intent: 'qualified' } };
        var out = { id: generateId(), type: 'record_outcome', x: 50, y: 280, config: { outcome_code: 'callback_booked', source: 'flow' } };
        setLocalNodes([t, intent, out]);
        setLocalEdges([
          { id: 'e1', source: t.id, target: intent.id },
          { id: 'e2', source: intent.id, target: out.id }
        ]);
        setDirty(true);
      } else if (tpl === 'support_auth_ticket') {
        ensureDemoIntegration(function (integrationId) {
          pushHistory();
          var t = { id: generateId(), type: 'trigger', x: 50, y: 50, config: { trigger_type: 'inbound_call_started' } };
          var h = { id: generateId(), type: 'http_request', x: 50, y: 160, config: { integration_id: integrationId, method: 'GET', path: '/get', responseMap: { ticketId: '$.args.ticket_id' } } };
          var tag = { id: generateId(), type: 'add_tag', x: 50, y: 280, config: { tag: 'authenticated' } };
          var out = { id: generateId(), type: 'record_outcome', x: 50, y: 400, config: { outcome_code: 'ticket_lookup', source: 'flow' } };
          setLocalNodes([t, h, tag, out]);
          setLocalEdges([
            { id: 'e1', source: t.id, target: h.id },
            { id: 'e2', source: h.id, target: tag.id },
            { id: 'e3', source: tag.id, target: out.id }
          ]);
          setDirty(true);
        });
      }
    }

    var selectedNode = localNodes.find(function (n) { return n.id === selectedId; }) || null;

    var triggerNode = localNodes.find(function (n) { return n.type === 'trigger'; });
    var derivedTriggerType = triggerNode && triggerNode.config && triggerNode.config.trigger_type
      ? triggerNode.config.trigger_type
      : null;
    var hasValidTrigger = !!triggerNode;
    var hasAnyNodes = localNodes.length > 0;
    var canSaveOrTest = hasValidTrigger && hasAnyNodes;

    var paletteItems = [
      { type: 'trigger', label: 'Trigger' },
      { type: 'http_request', label: 'HTTP Request' },
      { type: 'set_variable', label: 'Set Variable' },
      { type: 'condition', label: 'Condition' },
      { type: 'knowledge_pack', label: 'Knowledge Pack' },
      { type: 'record_outcome', label: 'Record Outcome' },
      { type: 'add_tag', label: 'Add Tag' },
      { type: 'set_intent', label: 'Set Intent' },
      { type: 'hubspot_upsert_contact', label: 'HubSpot: Upsert Contact' },
      { type: 'hubspot_log_call', label: 'HubSpot: Log Call' },
      { type: 'outlook_create_event', label: 'Outlook: Create Event' },
      { type: 'webhook_post', label: 'Webhook: POST JSON' }
    ];
    function getNodeHelp(type) {
      var meta = NODE_TYPES[type];
      return (meta && meta.help) ? meta.help : '';
    }

    return React.createElement('div', { className: 'flow-builder' },
      !hasValidTrigger ? React.createElement('div', { className: 'flow-trigger-warning' },
        React.createElement('span', { className: 'flow-trigger-warning-icon' }, '⚠'),
        React.createElement('span', null, 'This flow needs a Trigger. Add a Trigger node from the left panel to get started.')
      ) : null,
      React.createElement('div', { className: 'flow-toolbar' },
        React.createElement('button', { className: 'button outline small', onClick: onBack }, '← Back'),
        React.createElement('div', { className: 'flow-name-with-tooltip', style: { display: 'flex', alignItems: 'center', marginLeft: 12 } },
          React.createElement('input', {
            type: 'text',
            className: 'input',
            value: name,
            onChange: function (e) { setName(e.target.value); setDirty(true); },
            placeholder: 'Flow name',
            'aria-label': 'Flow name',
            style: { width: 180 },
            title: 'Flows let you define if-this-then-that logic for calls using integrations and AI.'
          }),
          React.createElement('span', {
            className: 'flow-name-help',
            title: 'Flows let you define if-this-then-that logic for calls using integrations and AI.'
          }, '?')
        ),
        hasValidTrigger ? React.createElement('span', { className: 'flow-trigger-readonly', style: { marginLeft: 12, fontSize: '0.8rem', color: 'var(--text-muted)' } },
          'Trigger: ', getTriggerLabel(derivedTriggerType)
        ) : null,
        React.createElement('select', {
          value: direction,
          'aria-label': 'Call direction',
          onChange: function (e) { setDirection(e.target.value); setDirty(true); },
          style: { marginLeft: 8 }
        },
          React.createElement('option', { value: 'inbound' }, 'Inbound'),
          React.createElement('option', { value: 'outbound' }, 'Outbound'),
          React.createElement('option', { value: 'both' }, 'Both')
        ),
        React.createElement('label', { style: { marginLeft: 12, display: 'flex', alignItems: 'center', gap: 6 } },
          React.createElement('input', { type: 'checkbox', checked: isEnabled, onChange: function (e) { setIsEnabled(e.target.checked); setDirty(true); } }),
          React.createElement('span', null, 'Enabled')
        ),
        React.createElement('div', { style: { flex: 1 } }),
        React.createElement('select', {
          'aria-label': 'Flow template',
          onChange: function (e) {
            var v = e.target.value;
            if (v) { applyTemplate(v); e.target.value = ''; }
          },
          style: { marginRight: 8 },
          defaultValue: ''
        },
          React.createElement('option', { value: '' }, 'Templates…'),
          React.createElement('option', { value: 'inbound_auth_fetch' }, 'Inbound → Authenticate → Fetch Data'),
          React.createElement('option', { value: 'outbound_notify' }, 'Outbound → Notify External System'),
          React.createElement('option', { value: 'inbound_auth' }, 'Inbound Auth + Fetch (simple)'),
          React.createElement('option', { value: 'outbound_webhook' }, 'Outbound → Webhook (simple)'),
          React.createElement('option', { value: 'utilities_auth_bill' }, 'Utilities — Authenticate + bill lookup'),
          React.createElement('option', { value: 'sales_qualify_callback' }, 'Sales — Qualify + book callback'),
          React.createElement('option', { value: 'support_auth_ticket' }, 'Support — Authenticate + ticket lookup')
        ),
        React.createElement('button', { className: 'button outline small', onClick: undo }, 'Undo'),
        React.createElement('button', { className: 'button outline small', onClick: redo, style: { marginLeft: 4 } }, 'Redo'),
        React.createElement('button', { className: 'button primary small', onClick: save, disabled: !dirty || !canSaveOrTest }, dirty ? 'Save' : 'Saved'),
        React.createElement('button', { className: 'button outline small', onClick: runTest, disabled: !canSaveOrTest, style: { marginLeft: 8 } }, 'Test Run')
      ),
      saveError ? React.createElement('div', { className: 'form-error', style: { margin: '0 16px 8px' } }, saveError) : null,
      testResult ? React.createElement('div', {
        className: 'flow-test-result' + (testResult.ok ? ' flow-test-result--ok' : ' flow-test-result--err'),
        style: { margin: '0 16px 8px', padding: 8, borderRadius: 8 }
      },
        testResult.ok ? React.createElement('span', null, 'Test passed. ctx.vars: ', JSON.stringify(testResult.ctx && testResult.ctx.vars)) : React.createElement('span', null, testResult.error || 'Test failed')
      ) : null,
      React.createElement('div', { className: 'flow-builder-body' },
        React.createElement('div', { className: 'flow-palette' },
          React.createElement('div', { className: 'flow-palette-title' }, 'Nodes'),
          paletteItems.map(function (p) {
            return React.createElement('div', {
              key: p.type,
              className: 'flow-palette-item',
              draggable: true,
              onDragStart: function (e) {
                e.dataTransfer.setData('application/json', JSON.stringify({ type: p.type }));
                e.dataTransfer.effectAllowed = 'copy';
              }
            },
              React.createElement('div', { className: 'flow-palette-item-label' }, p.label),
              React.createElement('div', { className: 'flow-palette-item-help' }, getNodeHelp(p.type))
            );
          })
        ),
        React.createElement('div', { className: 'flow-canvas-wrap' },
          React.createElement(FlowCanvas, {
            nodes: localNodes,
            edges: localEdges,
            selectedId: selectedId,
            selectedEdgeId: selectedEdgeId,
            onSelect: function (n) { setSelectedId(n ? n.id : null); setSelectedEdgeId(null); },
            onSelectEdge: setSelectedEdgeId,
            onUpdateNode: updateNode,
            onDrop: function (type, x, y) {
              if (type) addNode(type, x, y);
            },
            onConnect: addEdge,
            onDeleteEdge: deleteEdge,
            validateNode: validateNode
          })
        ),
        React.createElement('div', { className: 'flow-inspector-wrap' },
          selectedEdgeId ? React.createElement('div', { className: 'flow-inspector flow-inspector-edge' },
            React.createElement('h4', null, 'Connection'),
            React.createElement('button', {
              className: 'button danger small',
              onClick: function () { deleteEdge(selectedEdgeId); setSelectedEdgeId(null); }
            }, 'Delete connection')
          ) : null,
          React.createElement(NodeInspector, {
            node: selectedNode,
            integrations: integrations,
            onChange: function (updated) {
              updateNode(updated.id, updated);
            },
            onNavigate: props.onNavigate
          }),
          selectedNode ? React.createElement('button', {
            className: 'button danger small',
            style: { marginTop: 8 },
            onClick: function () { deleteNode(selectedNode.id); }
          }, 'Delete node') : null
        )
      )
    );
  }

  function FlowRunsList(props) {
    var flows = props.flows || [];
    var _s0 = useState(null);
    var selectedFlowId = _s0[0];
    var setSelectedFlowId = _s0[1];
    var _s1 = useState([]);
    var runs = _s1[0];
    var setRuns = _s1[1];
    var _s2 = useState(false);
    var loading = _s2[0];
    var setLoading = _s2[1];

    useEffect(function () {
      if (!selectedFlowId) { setRuns([]); return; }
      setLoading(true);
      API('GET', '/api/flows/' + selectedFlowId + '/runs?limit=30').then(function (r) {
        setRuns(Array.isArray(r && r.items) ? r.items : []);
      }).catch(function () { setRuns([]); }).finally(function () { setLoading(false); });
    }, [selectedFlowId]);

    return React.createElement('div', null,
      React.createElement('label', { className: 'field', style: { marginBottom: 8 } },
        React.createElement('span', { className: 'field-label' }, 'Select flow'),
        React.createElement('select', {
          value: selectedFlowId || '',
          onChange: function (e) { setSelectedFlowId(e.target.value ? parseInt(e.target.value, 10) : null); }
        },
          React.createElement('option', { value: '' }, '—'),
          (Array.isArray(flows) ? flows : []).map(function (f) {
            return React.createElement('option', { key: f.id, value: f.id }, f.name);
          })
        )
      ),
      loading ? React.createElement('div', { className: 'muted' }, 'Loading…') : runs.length === 0 ? React.createElement('div', { className: 'muted' }, 'No runs yet.') : React.createElement('div', { className: 'table-wrapper' },
        React.createElement('table', { className: 'table' },
          React.createElement('thead', null,
            React.createElement('tr', null,
              React.createElement('th', null, 'Started'),
              React.createElement('th', null, 'Status'),
              React.createElement('th', null, 'Error')
            )
          ),
          React.createElement('tbody', null,
            runs.map(function (r) {
              return React.createElement('tr', { key: r.id },
                React.createElement('td', null, formatSafeDate(r.started_at)),
                React.createElement('td', null, r.status),
                React.createElement('td', null, r.error || '—')
              );
            }            )
          )
        )
      ),
      React.createElement('div', { className: 'muted', style: { fontSize: '0.75rem', marginTop: 8 } },
        'Times shown in ' + zoneLabel(getWorkspaceTimeZone()) + '.')
    );
  }

  function FlowsView(props) {
    if (props && props.timezone) setWorkspaceTimeZone(props.timezone);
    var _useState12 = useState([]);
    var flows = _useState12[0];
    var setFlows = _useState12[1];
    var _useState13 = useState([]);
    var integrations = _useState13[0];
    var setIntegrations = _useState13[1];
    var _useState14 = useState(true);
    var loading = _useState14[0];
    var setLoading = _useState14[1];
    var _useState15 = useState(null);
    var editingFlow = _useState15[0];
    var setEditingFlow = _useState15[1];
    var _useState16 = useState('flows');
    var subTab = _useState16[0];
    var setSubTab = _useState16[1];
    var _useState17 = useState(null);
    var usage = _useState17[0];
    var setUsage = _useState17[1];
    var _useState18 = useState('');
    var listError = _useState18[0];
    var setListError = _useState18[1];

    function load() {
      setLoading(true);
      return Promise.all([
        API('GET', '/api/flows'),
        API('GET', '/api/external-integrations'),
        API('GET', '/api/flows/usage/today')
      ]).then(function (results) {
        setListError('');
        setFlows(Array.isArray(results[0] && results[0].items) ? results[0].items : []);
        setIntegrations(Array.isArray(results[1] && results[1].items) ? results[1].items : []);
        setUsage(results[2] || null);
      }).catch(function (e) {
        console.error(e);
        setFlows([]);
        setListError((e && e.message) || 'Could not load flows. Try again.');
      }).finally(function () {
        setLoading(false);
      });
    }

    useEffect(function () { load(); }, []);

    if (editingFlow !== null) {
      return React.createElement(FlowBuilderView, {
        flow: editingFlow,
        integrations: integrations,
        onBack: function () { setEditingFlow(null); load(); },
        onSaved: function (saved) { setEditingFlow(saved); },
        onNavigate: props.onNavigate,
        onIntegrationsRefresh: function () {
          API('GET', '/api/external-integrations').then(function (r) {
            setIntegrations(Array.isArray(r && r.items) ? r.items : []);
          }).catch(function () {});
        }
      });
    }

    return React.createElement('section', { className: 'card flows-card' },
      React.createElement('div', { className: 'card-header' },
        React.createElement('div', null,
          React.createElement('div', { className: 'card-title' }, 'Flows'),
          React.createElement('div', { className: 'card-subtitle' }, 'Build automation workflows for inbound/outbound calls. Drag nodes, connect, and test.')
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
          usage ? React.createElement('span', { className: 'muted', style: { fontSize: '0.75rem' } }, 'Runs today: ' + usage.runsToday + ' / ' + usage.limit) : null,
          React.createElement('button', { className: 'button primary', onClick: function () { setEditingFlow({ name: 'New Flow', trigger_type: 'inbound_call_started', direction: 'both', is_enabled: 1, graph_json: { nodes: [], edges: [] } }); } }, 'Create flow')
        )
      ),
      React.createElement('div', { className: 'card-body' },
        React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 12 } },
          React.createElement('button', { className: 'button small ' + (subTab === 'flows' ? 'primary' : 'outline'), onClick: function () { setSubTab('flows'); } }, 'Flows'),
          React.createElement('button', { className: 'button small ' + (subTab === 'runs' ? 'primary' : 'outline'), onClick: function () { setSubTab('runs'); } }, 'Run History')
        ),
        loading ? React.createElement('div', { className: 'muted' }, 'Loading…') : listError ? React.createElement('div', { className: 'form-error', role: 'alert' },
          listError,
          React.createElement('div', { style: { marginTop: 8 } },
            React.createElement('button', { type: 'button', className: 'button small outline', onClick: load }, 'Try again')
          )
        ) : subTab === 'flows' ? (
          flows.length === 0 ? React.createElement('div', { className: 'muted' }, 'No flows yet. Click Create flow to build your first automation.') : React.createElement('div', { className: 'table-wrapper' },
            React.createElement('table', { className: 'table' },
              React.createElement('thead', null,
                React.createElement('tr', null,
                  React.createElement('th', null, 'Name'),
                  React.createElement('th', null, 'Trigger'),
                  React.createElement('th', null, 'Direction'),
                  React.createElement('th', null, 'Enabled'),
                  React.createElement('th', null, 'Actions')
                )
              ),
              React.createElement('tbody', null,
                (Array.isArray(flows) ? flows : []).map(function (f) {
                  return React.createElement('tr', { key: f.id },
                    React.createElement('td', null, f.name),
                    React.createElement('td', null, getTriggerLabel(f.trigger_type)),
                    React.createElement('td', null, f.direction ? (f.direction.charAt(0).toUpperCase() + f.direction.slice(1)) : '—'),
                    React.createElement('td', null, f.is_enabled ? 'Yes' : 'No'),
                    React.createElement('td', null,
                      React.createElement('button', { className: 'button small outline', onClick: function () { setEditingFlow(f); } }, 'Edit'),
                      React.createElement('button', { className: 'button small outline', style: { marginLeft: 4 }, onClick: function () {
                        if (!confirm('Delete this flow?')) return;
                        var fid = f.id;
                        setFlows(function (prev) { return (Array.isArray(prev) ? prev : []).filter(function (x) { return x.id !== fid; }); });
                        API('DELETE', '/api/flows/' + fid).then(function () { return load(); }).catch(function (err) {
                          console.error(err);
                          var msg = (err && err.message) || 'Could not delete that flow.';
                          Promise.resolve(load()).then(function () { setListError(msg); });
                        });
                      } }, 'Delete')
                    )
                  );
                })
              )
            )
          )
        ) : React.createElement(FlowRunsList, { flows: flows })
      )
    );
  }

  window.FlowsView = FlowsView;
})();
